/**
 * 合体時の視覚演出（拡大縮小・光の粒子・得点表示・画面振動）。
 * 設定で振動をオフにできる。
 */

import Phaser from 'phaser';

import { isMotionReduced, shouldShake } from './shakePolicy';

import { GAME_TUNING } from '../config/gameConfig';
import type { GameSettings } from '../config/gameConfig';
import type { StageItemDefinition } from '../config/types';

export const SPARK_TEXTURE_KEY = 'fx-spark';

export class MergeEffects {
  private readonly scene: Phaser.Scene;
  private readonly settings: GameSettings;
  /** 追悼・慰霊・聖域の景色が映っているあいだは true。画面を揺らさない。 */
  private solemn = false;

  constructor(scene: Phaser.Scene, settings: GameSettings) {
    this.scene = scene;
    this.settings = settings;
  }

  private lastImpactFxAt = 0;

  /** 動きを減らすか（設定・端末のどちらか）。光・輪・粒・揺れを止める。 */
  private get calm(): boolean {
    return isMotionReduced(this.settings);
  }

  private get shakeAllowed(): boolean {
    return shouldShake({
      showShake: this.settings.showShake,
      solemn: this.solemn,
      reduceMotion: this.calm,
    });
  }

  /** 背景が「静かに扱うべき景色」に変わったことを伝える。 */
  setSolemn(solemn: boolean): void {
    this.solemn = solemn;
  }

  /** 光の粒子（簡易エフェクト）。 */
  burst(x: number, y: number, tint: number, scale = 1): void {
    if (this.calm) return;
    if (!this.scene.textures.exists(SPARK_TEXTURE_KEY)) return;
    const emitter = this.scene.add.particles(x, y, SPARK_TEXTURE_KEY, {
      lifespan: 420,
      speed: { min: 40 * scale, max: 150 * scale },
      scale: { start: 0.55 * scale, end: 0 },
      alpha: { start: 0.95, end: 0 },
      blendMode: 'ADD',
      tint,
      emitting: false,
    });
    emitter.setDepth(60);
    // Phaser 3.60 以降、explode の x, y は「発生器からの相対位置」。
    // 発生器はもう (x, y) に置いてあるので 0, 0 を渡す。
    // （以前は x, y を渡していて、粒子が右下の画面外に出ていた＝合体の光が見えていなかった）
    emitter.explode(GAME_TUNING.merge.particleCount, 0, 0);
    this.scene.time.delayedCall(600, () => emitter.destroy());
  }

  /** 加算得点のポップアップ。 */
  scorePop(x: number, y: number, gained: number, combo: number): void {
    // 別の手の合体も数えている（前の合体から少しのあいだ）ので「連鎖」ではなく「コンボ」と呼ぶ
    const label = combo >= 2 ? `+${gained}  コンボ${combo}!` : `+${gained}`;
    const text = this.scene.add
      .text(x, y, label, {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: combo >= 2 ? '26px' : '22px',
        color: combo >= 2 ? '#ffe27a' : '#fff8ee',
        stroke: '#4a1b12',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(70);

    this.scene.tweens.add({
      targets: text,
      y: y - 46,
      alpha: 0,
      duration: GAME_TUNING.effects.scorePopDurationMs,
      ease: 'Quad.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  /**
   * ぶつかった瞬間の小さな波紋と粉。
   * 強い接触だけ・間隔をあけて出す（積み上がった山で連発すると画面がうるさい）。
   */
  /** 出したら true（音もこれに合わせて鳴らす）。 */
  playImpact(x: number, y: number, strength: number, color: number): boolean {
    if (strength < GAME_TUNING.effects.impactFxMinStrength) return false;
    const now = this.scene.time.now;
    if (now - this.lastImpactFxAt < GAME_TUNING.effects.impactFxCooldownMs) return false;
    this.lastImpactFxAt = now;
    // 動きを減らす設定では、見た目は出さず音だけ鳴らす
    if (this.calm) return true;
    const s = Phaser.Math.Clamp(strength, 0, 1.4);
    this.ring(x, y, 6, 18 + 22 * s, 0xfff3d6, 0.55 * s + 0.2, 2 + 2 * s, 220);
    if (!this.scene.textures.exists(SPARK_TEXTURE_KEY)) return true;
    const emitter = this.scene.add.particles(x, y, SPARK_TEXTURE_KEY, {
      lifespan: 260,
      speed: { min: 30, max: 90 * s + 30 },
      angle: { min: 180, max: 360 },
      gravityY: 260,
      scale: { start: 0.28 + 0.12 * s, end: 0 },
      alpha: { start: 0.8, end: 0 },
      tint: [0xffffff, color],
      emitting: false,
    });
    emitter.setDepth(58);
    emitter.explode(Math.round(3 + 4 * s), 0, 0);
    this.scene.time.delayedCall(360, () => emitter.destroy());
    return true;
  }

  /** 広がって消える輪。 */
  private ring(
    x: number,
    y: number,
    from: number,
    to: number,
    color: number,
    alpha: number,
    width: number,
    duration: number,
  ): void {
    if (this.calm) return;
    // 明るい昼の景色でも見えるよう、加算合成にせず、外側に薄い影の輪を重ねる
    const g = this.scene.add.graphics().setDepth(57);
    const holder = { r: from, a: alpha };
    const draw = () => {
      g.clear();
      g.lineStyle(width + 3, 0x3a1510, holder.a * 0.35);
      g.strokeCircle(x, y, holder.r + 1);
      g.lineStyle(width, color, holder.a);
      g.strokeCircle(x, y, holder.r);
    };
    draw();
    this.scene.tweens.add({
      targets: holder,
      r: to,
      a: 0,
      duration,
      ease: 'Cubic.easeOut',
      onUpdate: draw,
      onComplete: () => g.destroy(),
    });
  }

  /**
   * 料理の色の「しぶき」。スイカ系の果汁が飛ぶ演出にあたる。
   * 料理ごとの主色・差し色を使うので、あまおうは赤、ずんだは緑に散る。
   */
  private splash(x: number, y: number, item: StageItemDefinition, scale: number): void {
    if (this.calm) return;
    if (!this.scene.textures.exists(SPARK_TEXTURE_KEY)) return;
    const base = Phaser.Display.Color.HexStringToColor(item.icon.baseColor).color;
    const accent = Phaser.Display.Color.HexStringToColor(item.icon.accentColor).color;
    const emitter = this.scene.add.particles(x, y, SPARK_TEXTURE_KEY, {
      lifespan: { min: 450, max: 750 },
      speed: { min: 110 * scale, max: 300 * scale },
      gravityY: 560,
      scale: { start: 0.75 * scale, end: 0.1 },
      alpha: { start: 1, end: 0 },
      tint: [base, accent, base],
      emitting: false,
    });
    emitter.setDepth(59);
    emitter.explode(Math.round(14 + 8 * scale), 0, 0);
    this.scene.time.delayedCall(820, () => emitter.destroy());
  }

  /** 画面全体がほんのり光る（大きな合体だけ）。 */
  private flash(alpha: number): void {
    if (this.solemn || this.calm) return;
    const cam = this.scene.cameras.main;
    // 既定の強さ（1）だと真っ白になるので、始める前に抑えておく（start で読まれる）
    cam.flashEffect.alpha = alpha;
    cam.flash(180, 255, 244, 214, true);
  }

  /**
   * 料理の名前の帯。初めて作った料理と、Lv9以上の大きな料理で出す。
   * スイカ系で「何ができたか」が一瞬で分かる、というのが気持ちよさの芯にある。
   */
  banner(item: StageItemDefinition, isNew: boolean, isTop: boolean): void {
    const { width } = GAME_TUNING.world;
    const y = 190;
    const title = isTop ? '最上位の一品！' : isNew ? 'はじめて作った！' : `Lv.${item.level}`;
    const box = this.scene.add.container(width / 2, y).setDepth(80).setAlpha(0);
    const label = this.scene.add
      .text(0, -16, title, {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '16px',
        color: isTop ? '#ffe27a' : '#fff3d6',
        stroke: '#3a1510',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    const name = this.scene.add
      .text(0, 12, item.name, {
        fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", serif',
        fontSize: isTop ? '34px' : '28px',
        fontStyle: 'bold',
        color: '#fff8ee',
        stroke: '#3a1510',
        strokeThickness: 6,
      })
      .setOrigin(0.5);
    const w = Math.min(width - 24, Math.max(name.width, label.width) + 56);
    const bg = this.scene.add.graphics();
    bg.fillStyle(isTop ? 0x8d2027 : 0x2b1a17, 0.82);
    bg.fillRoundedRect(-w / 2, -38, w, 76, 14);
    bg.lineStyle(2, isTop ? 0xffe27a : 0xf2c14e, 0.9);
    bg.strokeRoundedRect(-w / 2, -38, w, 76, 14);
    box.add([bg, label, name]);
    this.scene.tweens.add({
      targets: box,
      alpha: 1,
      scale: this.calm ? 1 : { from: 0.7, to: 1 },
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: box,
          alpha: 0,
          y: this.calm ? y : y - 20,
          delay: isTop ? 1600 : 900,
          duration: 380,
          onComplete: () => box.destroy(),
        });
      },
    });
  }

  /**
   * 最上位の一品ができたときのカットイン。
   * 画面を暗くして、料理を大写し・後光・県名。1.8秒ほどで引く（物理は止めない）。
   */
  topCutIn(item: StageItemDefinition, textureKey: string, regionName: string): void {
    const { width, height } = GAME_TUNING.world;
    const cx = width / 2;
    const cy = height * 0.4;
    const layer = this.scene.add.container(0, 0).setDepth(90);
    const calm = this.calm;

    const dim = this.scene.add.rectangle(cx, height / 2, width, height, 0x0d0605, 0.62);
    // 後光：放射状の帯
    const rays = this.scene.add.graphics({ x: cx, y: cy }).setVisible(!calm);
    const rayCount = 14;
    for (let i = 0; i < rayCount; i += 1) {
      const a0 = (i / rayCount) * Math.PI * 2;
      const a1 = a0 + Math.PI / rayCount;
      rays.fillStyle(i % 2 === 0 ? 0xffe27a : 0xf2a93b, 0.22);
      rays.slice(0, 0, 300, a0, a1, false);
      rays.fillPath();
    }
    const glow = this.scene.add.circle(cx, cy, 120, 0xfff3c4, 0.25);
    const dish = this.scene.add.image(cx, cy, textureKey);
    const base = 210 / Math.max(dish.width, dish.height);
    dish.setScale(calm ? base : base * 0.3);

    const title = this.scene.add
      .text(cx, cy - 150, '最上位の一品！', {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: '#ffe27a',
        stroke: '#3a1510',
        strokeThickness: 5,
      })
      .setOrigin(0.5);
    const name = this.scene.add
      .text(cx, cy + 135, item.name, {
        fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", serif',
        fontSize: '36px',
        fontStyle: 'bold',
        color: '#fff8ee',
        stroke: '#3a1510',
        strokeThickness: 7,
        align: 'center',
        wordWrap: { width: width - 40 },
      })
      .setOrigin(0.5);
    const region = this.scene.add
      .text(cx, cy + 178, `${regionName}の頂点`, {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '17px',
        color: '#f2dcc0',
        stroke: '#3a1510',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    layer.add([dim, rays, glow, dish, title, name, region]);
    layer.setAlpha(0);

    this.scene.tweens.add({ targets: layer, alpha: 1, duration: 180 });
    if (!calm) {
      this.scene.tweens.add({ targets: dish, scale: base, duration: 520, ease: 'Back.easeOut' });
      this.scene.tweens.add({ targets: rays, angle: 40, duration: 2200, ease: 'Sine.easeOut' });
    }
    this.scene.tweens.add({
      targets: glow,
      scale: { from: 0.6, to: 1.25 },
      alpha: { from: 0.45, to: 0.15 },
      duration: calm ? 0 : 900,
      yoyo: !calm,
    });
    [title, name, region].forEach((t, i) =>
      this.scene.tweens.add({
        targets: t,
        alpha: { from: 0, to: 1 },
        y: calm ? t.y : { from: t.y + 14, to: t.y },
        delay: 200 + i * 120,
        duration: 320,
        ease: 'Cubic.easeOut',
      }),
    );
    this.burst(cx, cy, 0xffe27a, 2);
    this.flash(0.35);
    this.shake(1.6);
    this.scene.time.delayedCall(1900, () => {
      this.scene.tweens.add({
        targets: layer,
        alpha: 0,
        duration: 420,
        onComplete: () => layer.destroy(true),
      });
    });
  }

  /** 合体演出のまとめ実行。 */
  playMerge(params: {
    x: number;
    y: number;
    item: StageItemDefinition;
    gained: number;
    combo: number;
    radius?: number;
  }): void {
    const level = params.item.level;
    const tint = Phaser.Display.Color.HexStringToColor(params.item.icon.accentColor).color;
    const size = Math.min(2, 0.7 + level * 0.1);
    this.burst(params.x, params.y, tint, size);
    this.splash(params.x, params.y, params.item, Math.min(1.8, 0.6 + level * 0.1));
    // 衝撃波：生まれた料理の大きさから少し外へ広がる輪
    const r = params.radius ?? 20 + level * 4;
    this.ring(params.x, params.y, r * 0.6, r * 1.6 + 10, 0xfff3d6, 0.85, 3 + level * 0.3, 360);
    if (params.combo >= 2) {
      this.ring(params.x, params.y, r * 0.8, r * 2.2 + 20, 0xffe27a, 0.6, 2, 480);
    }
    if (level >= 9) this.flash(0.18 + (level - 9) * 0.06);
    this.scorePop(params.x, params.y, params.gained, params.combo);
    if (this.shakeAllowed && params.item.level >= 5) {
      this.shake(Math.min(1.8, 0.6 + params.item.level * 0.1));
    }
  }

  /** 最高レベル同士の接触ボーナス演出。 */
  playBonus(x: number, y: number, points: number): void {
    this.burst(x, y, 0xffe27a, 2);
    const text = this.scene.add
      .text(x, y, `BONUS +${points}`, {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '24px',
        color: '#ffe27a',
        stroke: '#4a1b12',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(70);
    this.scene.tweens.add({
      targets: text,
      y: y - 54,
      alpha: 0,
      duration: 780,
      onComplete: () => text.destroy(),
    });
    this.shake(2);
  }

  shake(strength = 1): void {
    // 追悼・慰霊・聖域の景色の上では揺らさない。設定より優先する。
    if (!this.shakeAllowed) return;
    this.scene.cameras.main.shake(
      GAME_TUNING.effects.shakeDurationMs,
      GAME_TUNING.effects.shakeIntensity * strength,
    );
  }
}
